"use client"

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import ReportService from '@/api/reports.service';
import {
  BarChart2,
  Building2,
  Calculator,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Users,
  FilePlus2,
  Eye,
  Download,
  FileSpreadsheet,
  FilePieChart
} from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function Reports() {
  const [activeTab, setActiveTab] = useState('financial');
  const [isLoading, setIsLoading] = useState(true);
  const [financialReports, setFinancialReports] = useState<any[]>([]);
  const [propertyReports, setPropertyReports] = useState<any[]>([]);
  const [tenantReports, setTenantReports] = useState<any[]>([]);
  const [maintenanceReports, setMaintenanceReports] = useState<any[]>([]);
  const [loanReports, setLoanReports] = useState<any[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;

  useEffect(() => {
    if (businessId && activeTab) {
      fetchReports(activeTab);
    }
  }, [businessId, activeTab]);

  const fetchReports = async (type: string) => {
    if (!businessId) return;
    
    setIsLoading(true);
    try {
      let response;
      switch (type) {
        case 'financial':
          response = await ReportService.getAllFinancialReports(businessId);
          if (response.data.result === 'success') {
            setFinancialReports(response.data.reports || []);
          }
          break;
        case 'property':
          response = await ReportService.getAllPropertyReports(businessId);
          if (response.data.result === 'success') {
            setPropertyReports(response.data.reports || []);
          }
          break;
        case 'tenant':
          response = await ReportService.getAllTenantReports(businessId);
          if (response.data.result === 'success') {
            setTenantReports(response.data.reports || []);
          }
          break;
        case 'maintenance':
          response = await ReportService.getAllMaintenanceReports(businessId);
          if (response.data.result === 'success') {
            setMaintenanceReports(response.data.reports || []);
          }
          break;
        case 'loan':
          response = await ReportService.getAllLoanReports(businessId);
          if (response.data.result === 'success') {
            setLoanReports(response.data.reports || []);
          }
          break;
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast({
        title: "Error",
        description: "Failed to load reports.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      const response = await ReportService.deleteReport(reportToDelete);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Report deleted successfully",
        });
        
        // Refresh the reports
        fetchReports(activeTab);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to delete report",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting the report",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  const handleNavigateToCreate = (reportType: string) => {
    navigate(`/reports/create/${reportType}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary">Draft</Badge>;
      case 'generated':
        return <Badge className="bg-green-500/90 text-green-50 hover:bg-green-500">Generated</Badge>;
      case 'archived':
        return <Badge className="bg-amber-500 hover:bg-amber-600">Archived</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getReportIcon = (type: string, className: string = "w-4 h-4") => {
    switch (type) {
      case 'income_statement':
        return <DollarSign className={className} />;
      case 'balance_sheet':
        return <FileText className={className} />;
      case 'cash_flow':
        return <CreditCard className={className} />;
      case 'changes_in_equity':
        return <BarChart2 className={className} />;
      case 'rental_income':
        return <Building2 className={className} />;
      case 'occupancy':
      case 'vacancy':
        return <Building2 className={className} />;
      case 'tenant_ledger':
        return <FileSpreadsheet className={className} />;
      case 'aging_debtors':
        return <Calculator className={className} />;
      case 'maintenance':
        return <FilePlus2 className={className} />;
      case 'amortization':
        return <FilePieChart className={className} />;
      default:
        return <FileText className={className} />;
    }
  };

  const renderReportList = (reports: any[]) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (reports.length === 0) {
      return (
        <div className="text-center py-12">
          <BarChart2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No Reports Found</h3>
          <p className="text-muted-foreground mb-6">
            Create your first report to get started
          </p>
          <Button onClick={() => handleNavigateToCreate(activeTab)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Report
          </Button>
        </div>
      );
    }

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Name</TableHead>
              <TableHead>Report Type</TableHead>
              <TableHead>Date Range</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.publicId}>
                <TableCell className="font-medium">{report.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getReportIcon(report.reportType)}
                    <span>{report.reportType.replace(/_/g, ' ')}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {report.dateRange === 'custom' ? (
                    <span>
                      {format(new Date(report.startDate), "MMM d, yyyy")} - {format(new Date(report.endDate), "MMM d, yyyy")}
                    </span>
                  ) : (
                    <span>{report.dateRange}</span>
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(report.status)}</TableCell>
                <TableCell>{format(new Date(report.dateCreated), "MMM d, yyyy")}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-4 h-4"
                        >
                          <circle cx="12" cy="12" r="1" />
                          <circle cx="12" cy="5" r="1" />
                          <circle cx="12" cy="19" r="1" />
                        </svg>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="flex items-center gap-2"
                        onClick={() => navigate(`/report/${report.publicId}`)}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </DropdownMenuItem>
                      {report.status === 'generated' && (
                        <>
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onClick={() => {
                              ReportService.exportReportToPdf(report.publicId).then(response => {
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${report.name}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }).catch(error => {
                                console.error("Error downloading PDF:", error);
                                toast({
                                  title: "Error",
                                  description: "Failed to download PDF",
                                  variant: "destructive",
                                });
                              });
                            }}
                          >
                            <Download className="w-4 h-4" />
                            Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="flex items-center gap-2"
                            onClick={() => {
                              ReportService.exportReportToExcel(report.publicId).then(response => {
                                const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${report.name}.xlsx`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                              }).catch(error => {
                                console.error("Error downloading Excel:", error);
                                toast({
                                  title: "Error",
                                  description: "Failed to download Excel file",
                                  variant: "destructive",
                                });
                              });
                            }}
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Export Excel
                          </DropdownMenuItem>
                        </>
                      )}
                      {report.status === 'draft' && (
                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onClick={() => {
                            ReportService.generateReport(report.publicId)
                              .then(response => {
                                if (response.data.result === 'success') {
                                  toast({
                                    title: "Success",
                                    description: "Report generated successfully",
                                  });
                                  fetchReports(activeTab);
                                } else {
                                  toast({
                                    title: "Error",
                                    description: response.data.message || "Failed to generate report",
                                    variant: "destructive",
                                  });
                                }
                              })
                              .catch(error => {
                                console.error("Error generating report:", error);
                                toast({
                                  title: "Error",
                                  description: "An error occurred while generating the report",
                                  variant: "destructive",
                                });
                              });
                          }}
                        >
                          <BarChart2 className="w-4 h-4" />
                          Generate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        className="flex items-center gap-2 text-destructive focus:text-destructive"
                        onClick={() => {
                          setReportToDelete(report.publicId);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Reports</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage various types of reports for your business
            </p>
          </div>
          
          <Button onClick={() => handleNavigateToCreate(activeTab)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Report
          </Button>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Reports</CardTitle>
            <CardDescription>
              View and manage your reports
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs defaultValue="financial" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 grid grid-cols-5 w-full">
                <TabsTrigger value="financial" className="flex items-center gap-2">
                  <BarChart2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Financial</span>
                </TabsTrigger>
                <TabsTrigger value="property" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Property</span>
                </TabsTrigger>
                <TabsTrigger value="tenant" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Tenant</span>
                </TabsTrigger>
                <TabsTrigger value="maintenance" className="flex items-center gap-2">
                  <FilePlus2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Maintenance</span>
                </TabsTrigger>
                <TabsTrigger value="loan" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="hidden sm:inline">Loan</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="financial">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <h3 className="text-lg font-medium">Financial Reports</h3>
                    <p className="text-sm text-muted-foreground">
                      Income statements, balance sheets, cash flow statements
                    </p>
                  </div>
                  {renderReportList(financialReports)}
                </div>
              </TabsContent>

              <TabsContent value="property">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <h3 className="text-lg font-medium">Property Reports</h3>
                    <p className="text-sm text-muted-foreground">
                      Rental income, occupancy reports, vacancy analysis
                    </p>
                  </div>
                  {renderReportList(propertyReports)}
                </div>
              </TabsContent>

              <TabsContent value="tenant">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <h3 className="text-lg font-medium">Tenant Reports</h3>
                    <p className="text-sm text-muted-foreground">
                      Tenant ledgers, aging of rental debtors
                    </p>
                  </div>
                  {renderReportList(tenantReports)}
                </div>
              </TabsContent>

              <TabsContent value="maintenance">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <h3 className="text-lg font-medium">Maintenance Reports</h3>
                    <p className="text-sm text-muted-foreground">
                      Maintenance, repairs, capital expenditure
                    </p>
                  </div>
                  {renderReportList(maintenanceReports)}
                </div>
              </TabsContent>

              <TabsContent value="loan">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
                    <h3 className="text-lg font-medium">Loan Reports</h3>
                    <p className="text-sm text-muted-foreground">
                      Loan/mortgage amortization schedules
                    </p>
                  </div>
                  {renderReportList(loanReports)}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the report and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReport}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 